type BrowserSessionData = {
  identity: {
    user: any;
    company_visited: any;
  };
  verify: {
    verifyToken: any;
    confirmationCode: any;
  };
  retryingFlag: {
    isRetrying: boolean;
  };
};

type SessionKey = keyof BrowserSessionData;

const memoryStore: BrowserSessionData = {
  identity: {
    user: {},
    company_visited: null,
  },
  verify: {
    verifyToken: null,
    confirmationCode: null,
  },
  retryingFlag: {
    isRetrying: false,
  },
};

const isBrowser = () => typeof window !== 'undefined';

const safeLocalStorageGet = (key: string): string | null => {
  try {
    if (!isBrowser()) return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string): boolean => {
  try {
    if (!isBrowser()) return false;
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const safeLocalStorageRemove = (key: string): boolean => {
  try {
    if (!isBrowser()) return false;
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

export const browserSession = {
  getIdentity() {
    const stored = safeLocalStorageGet('zenbox.identity');

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        memoryStore.identity = {
          user: parsed?.user ?? {},
          company_visited: parsed?.company_visited ?? null,
        };
        return clone(memoryStore.identity);
      } catch {
        return clone(memoryStore.identity);
      }
    }

    return clone(memoryStore.identity);
  },

  setIdentity(identity: any) {
    const normalizedIdentity = {
      user: identity?.user ?? identity ?? {},
      company_visited: identity?.company_visited ?? null,
    };

    memoryStore.identity = {
      user: normalizedIdentity.user,
      company_visited: normalizedIdentity.company_visited,
    };

    safeLocalStorageSet(
      'zenbox.identity',
      JSON.stringify({
        user: normalizedIdentity.user,
        company_visited: normalizedIdentity.company_visited,
      })
    );
  },

  updateCompanyVisited(company: any) {
    const currentIdentity = this.getIdentity();

    this.setIdentity({
      user: currentIdentity?.user ?? {},
      company_visited: company ?? null,
    });
  },

  clearIdentity() {
    memoryStore.identity = {
      user: {},
      company_visited: null,
    };

    safeLocalStorageRemove('zenbox.identity');
  },

  getVerify() {
    const stored = safeLocalStorageGet('zenbox.verify');

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        memoryStore.verify = {
          verifyToken: parsed?.verifyToken ?? null,
          confirmationCode: parsed?.confirmationCode ?? null,
        };
        return clone(memoryStore.verify);
      } catch {
        return clone(memoryStore.verify);
      }
    }

    return clone(memoryStore.verify);
  },

  setVerify(data: { verifyToken?: any; confirmationCode?: any }) {
    memoryStore.verify = {
      verifyToken: data?.verifyToken ?? null,
      confirmationCode: data?.confirmationCode ?? null,
    };

    safeLocalStorageSet(
      'zenbox.verify',
      JSON.stringify({
        verifyToken: data?.verifyToken ?? null,
        confirmationCode: data?.confirmationCode ?? null,
      })
    );
  },

  clearVerify() {
    memoryStore.verify = {
      verifyToken: null,
      confirmationCode: null,
    };

    safeLocalStorageRemove('zenbox.verify');
  },

  getRetryingFlag() {
    const stored = safeLocalStorageGet('zenbox.retryingFlag');

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        memoryStore.retryingFlag = {
          isRetrying: !!parsed?.isRetrying,
        };
        return clone(memoryStore.retryingFlag);
      } catch {
        return clone(memoryStore.retryingFlag);
      }
    }

    return clone(memoryStore.retryingFlag);
  },

  setRetryingFlag(data: { isRetrying: boolean }) {
    memoryStore.retryingFlag = {
      isRetrying: !!data?.isRetrying,
    };

    safeLocalStorageSet(
      'zenbox.retryingFlag',
      JSON.stringify({
        isRetrying: !!data?.isRetrying,
      })
    );
  },

  clearRetryingFlag() {
    memoryStore.retryingFlag = {
      isRetrying: false,
    };

    safeLocalStorageRemove('zenbox.retryingFlag');
  },

  clearQuestion() {
    safeLocalStorageRemove('zenbox.question');
  },

  clearAllAuthData() {
    this.clearIdentity();
    this.clearVerify();
    this.clearRetryingFlag();
    this.clearQuestion();
  },

  isLocalStorageAvailable() {
    try {
      if (!isBrowser()) return false;
      const key = '__zenbox_storage_test__';
      window.localStorage.setItem(key, '1');
      window.localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  },
};
